/**
 * Expanded gift card view, inspector-style detail sheet consistent with the
 * Inventory / Orders / Trade-ins pages: identity header with actions, flag
 * chips, key facts, Gift Card (with number/PIN reveal) / Association /
 * Customer sections, and a tabbed activity region (history + audited reveal
 * history). Field and action parity with reware-admin's gift card detail.
 */
import { useRecordContext } from 'react-admin';
import { Box, Chip, Stack, Typography } from '@mui/material';

import { CopyButton, displayNameFor, semanticStateColor } from '@src/components/indexPage';

import { formatMaskedNumber } from './format';
import GiftcardActions from './GiftcardActions';
import GiftcardActivityTabs from './GiftcardActivityTabs';
import {
  AssociationSection,
  CustomerSection,
  GiftCardSection,
  GiftcardFlags,
  GiftcardKeyFacts,
} from './GiftcardDetailSections';
import type { ConstantOption, GiftcardRecord } from './types';

interface GiftcardExpandProps {
  states: ConstantOption[];
  sources: ConstantOption[];
  reasons: ConstantOption[];
}

export const GiftcardExpand = ({ states, sources, reasons }: GiftcardExpandProps) => {
  const gc = useRecordContext<GiftcardRecord>();
  if (!gc) return null;

  return (
    <Box sx={{ bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider', p: 2.5 }}>
      <Stack spacing={2}>
        {/* Identity header + actions */}
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'flex-start' }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontSize: 16, lineHeight: 1.3 }}>
                {`Gift card ${formatMaskedNumber(gc.number)}`}
              </Typography>
              <Chip size="small" color={semanticStateColor(gc.state)} label={displayNameFor(states, gc.state)} />
              <Chip size="small" variant="outlined" label={displayNameFor(sources, gc.source)} />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap', mt: 0.5, color: 'text.secondary' }}>
              <Typography variant="caption">
                ID
                {' '}
                {gc.id}
                <CopyButton name="Gift card ID" value={String(gc.id)} />
              </Typography>
              <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                {`gc-${gc.uuid}`}
                <CopyButton name="Gift card UUID" value={`gc-${gc.uuid}`} />
              </Typography>
            </Stack>
          </Box>
          <GiftcardActions gc={gc} />
        </Stack>

        <GiftcardFlags gc={gc} />

        <GiftcardKeyFacts gc={gc} states={states} />

        {/* Detail sections */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(230px, 1fr))' },
            columnGap: 4,
            rowGap: 2.5,
            alignContent: 'start',
          }}
        >
          <GiftCardSection gc={gc} sources={sources} reasons={reasons} />
          <AssociationSection gc={gc} />
          <CustomerSection gc={gc} />
        </Box>

        <GiftcardActivityTabs gc={gc} />
      </Stack>
    </Box>
  );
};

export default GiftcardExpand;
